/**
 * Voice input hook for recording and transcribing audio
 *
 * Uses gpt-4o-transcribe for STT
 * The transcript is shown in the input field for user verification before sending
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { getMicrophoneSupportError } from '../utils/mediaSupport';

// Use relative paths for ngrok/proxy, absolute for direct localhost access
const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const API_BASE_URL = process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:5000' : '');
const WAVEFORM_BAR_COUNT = 80;

const createBaseWaveform = (): number[] =>
  Array.from({ length: WAVEFORM_BAR_COUNT }, () => 0.06);

interface UseVoiceInputReturn {
  isRecording: boolean;
  isTranscribing: boolean;
  error: string | null;
  waveformData: number[];
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | null>;
  cancelRecording: () => void;
}

interface VoiceScope {
  customerSlug?: string;
  agentSlug?: string;
}

export function useVoiceInput(scope: VoiceScope = {}): UseVoiceInputReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waveformData, setWaveformData] = useState<number[]>(createBaseWaveform);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const waveformFrameRef = useRef<number | null>(null);
  const lastWaveformUpdateRef = useRef<number>(0);

  const stopWaveformAnalysis = useCallback(() => {
    if (waveformFrameRef.current !== null) {
      cancelAnimationFrame(waveformFrameRef.current);
      waveformFrameRef.current = null;
    }

    if (mediaSourceRef.current) {
      mediaSourceRef.current.disconnect();
      mediaSourceRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    if (audioContextRef.current) {
      const context = audioContextRef.current;
      audioContextRef.current = null;
      void context.close().catch(() => undefined);
    }

    setWaveformData(createBaseWaveform());
    lastWaveformUpdateRef.current = 0;
  }, []);

  const startWaveformAnalysis = useCallback((stream: MediaStream) => {
    stopWaveformAnalysis();

    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) {
      return;
    }

    try {
      const context = new AudioContextCtor();
      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.82;
      source.connect(analyser);

      audioContextRef.current = context;
      mediaSourceRef.current = source;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const drawFrame = () => {
        analyser.getByteFrequencyData(dataArray);
        const now = performance.now();

        if (now - lastWaveformUpdateRef.current >= 33) {
          lastWaveformUpdateRef.current = now;
          setWaveformData((prev) => {
            const bars = new Array<number>(WAVEFORM_BAR_COUNT).fill(0);
            const binsPerBar = Math.max(1, Math.floor(dataArray.length / WAVEFORM_BAR_COUNT));

            for (let i = 0; i < WAVEFORM_BAR_COUNT; i++) {
              const start = i * binsPerBar;
              const end = i === WAVEFORM_BAR_COUNT - 1
                ? dataArray.length
                : Math.min(dataArray.length, start + binsPerBar);

              let sum = 0;
              for (let j = start; j < end; j++) {
                sum += dataArray[j];
              }

              const avg = end > start ? sum / (end - start) : 0;
              const normalized = Math.pow(avg / 255, 0.7);
              const floor = 0.05;
              const target = Math.min(1, floor + normalized * 1.25);
              const previous = prev[i] ?? floor;
              bars[i] = previous * 0.62 + target * 0.38;
            }

            return bars;
          });
        }

        waveformFrameRef.current = requestAnimationFrame(drawFrame);
      };

      drawFrame();
    } catch (err) {
      console.error('🎤 [MIC] Waveform analysis setup failed:', err);
      stopWaveformAnalysis();
    }
  }, [stopWaveformAnalysis]);

  useEffect(() => {
    return () => {
      stopWaveformAnalysis();
    };
  }, [stopWaveformAnalysis]);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      console.log(`🎤 [MIC] Start recording clicked at ${new Date().toISOString()}`);

      const supportError = getMicrophoneSupportError();
      if (supportError) {
        throw new Error(supportError);
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Use webm format for browser compatibility
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      startWaveformAnalysis(stream);

      mediaRecorder.ondataavailable = (event) => {
        console.log(`🎤 [MIC] Audio chunk received: ${event.data.size} bytes`);
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Start with timeslice to collect data every 250ms
      mediaRecorder.start(250);
      console.log(`🎤 [MIC] Recording started with mimeType: ${mimeType}`);
      setIsRecording(true);
    } catch (err) {
      console.error('🎤 [MIC] Error starting recording:', err);
      setError(err instanceof Error ? err.message : 'Could not access microphone');
      stopWaveformAnalysis();
    }
  }, [startWaveformAnalysis, stopWaveformAnalysis]);

  const stopRecording = useCallback(async (): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current || !isRecording) {
        resolve(null);
        return;
      }

      const mediaRecorder = mediaRecorderRef.current;
      console.log(`🎤 [MIC] Stop recording clicked at ${new Date().toISOString()}`);

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        mediaRecorder.stream.getTracks().forEach((track) => track.stop());
        stopWaveformAnalysis();

        console.log(`🎤 [MIC] Recording stopped, chunks: ${audioChunksRef.current.length}`);

        if (audioChunksRef.current.length === 0) {
          console.log('🎤 [MIC] No audio chunks collected');
          setIsRecording(false);
          resolve(null);
          return;
        }

        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType,
        });

        console.log(`🎤 [MIC] Audio blob created: ${audioBlob.size} bytes, type: ${audioBlob.type}`);

        setIsRecording(false);
        setIsTranscribing(true);

        const startTime = performance.now();

        try {
          // Send to transcription API
          const formData = new FormData();
          // Use appropriate extension based on mime type
          const extension = mediaRecorder.mimeType.includes('webm') ? 'webm' : 'mp4';
          formData.append('audio', audioBlob, `recording.${extension}`);
          // Default to German for better transcription accuracy
          formData.append('language', 'de');
          if (scope.customerSlug) formData.append('customer_slug', scope.customerSlug);
          if (scope.agentSlug) formData.append('agent_slug', scope.agentSlug);
          const adminToken = sessionStorage.getItem('admin_token');

          console.log(`🎤 [MIC] Calling ${API_BASE_URL}/api/voice/transcribe (STT) with language=de`);
          const response = await fetch(`${API_BASE_URL}/api/voice/transcribe`, {
            method: 'POST',
            headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : undefined,
            body: formData,
          });

          const endTime = performance.now();
          const duration = (endTime - startTime).toFixed(0);
          console.log(`🎤 [MIC] STT API response received in ${duration}ms - status: ${response.status}`);

          const result = await response.json();
          console.log(`🎤 [MIC] STT result: success=${result.success}, transcript length=${result.transcript?.length || 0}`);

          if (result.success && result.transcript) {
            console.log(`🎤 [MIC] Transcript: "${result.transcript}"`);
            setIsTranscribing(false);
            resolve(result.transcript);
          } else {
            console.error(`🎤 [MIC] Transcription failed: ${result.error}`);
            setError(result.error || 'Transcription failed');
            setIsTranscribing(false);
            resolve(null);
          }
        } catch (err) {
          console.error('🎤 [MIC] Transcription error:', err);
          setError('Failed to transcribe audio');
          setIsTranscribing(false);
          resolve(null);
        }
      };

      mediaRecorder.stop();
    });
  }, [isRecording, scope.agentSlug, scope.customerSlug, stopWaveformAnalysis]);

  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      const recorder = mediaRecorderRef.current;
      recorder.stream.getTracks().forEach((track) => track.stop());
      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      setIsRecording(false);
      stopWaveformAnalysis();
    }
  }, [isRecording, stopWaveformAnalysis]);

  return {
    isRecording,
    isTranscribing,
    error,
    waveformData,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
